import { useCallback, useEffect, useRef, useState } from 'react';
import {
  planUploadNames,
  type IngestBatch,
  type IngestBatchSkipReason,
  type UploadPlan,
} from '@kennisbank/shared';
import { apiPost } from '../api/client.js';
import {
  chunk,
  classifyPlanned,
  resolveUploads,
  runWithConcurrency,
  type CollisionChoice,
  type DuplicateCheckItem,
} from '../lib/bulk-upload.js';
import { runBulkUpload, type BulkUploadCommon } from '../lib/bulk-upload-runner.js';
import { md5Base64OfBlob } from '../lib/md5.js';
import type {
  PreflightPlannedFile,
  PreflightState,
} from '../components/admin/BulkUploadPreflight.js';
import type { LocalBatchUpload } from '../components/admin/BatchProgressPanel.js';

/**
 * Folder / multi-file upload flow for the Knowledgebase Admin page:
 *
 *   drop → plan names (sanitise, subfolder de-dup, skip unsupported) →
 *   fingerprint (md5) → server duplicate check → pre-flight modal →
 *   create batch → upload groups (runner) → server queue takes over.
 */

export interface DroppedFile {
  file: File;
  /** Path inside the dropped folder, e.g. `Policies/2024/a.pdf`; name for loose files. */
  relativePath: string;
}

const HASH_CONCURRENCY = 2;
const CHECK_CHUNK = 500;
const MAX_SKIPPED_RECORDED = 2000;

interface PendingContext {
  items: DroppedFile[];
  plan: UploadPlan;
  planned: PreflightPlannedFile[];
  dispositions: ReturnType<typeof classifyPlanned>;
  takenNames: Set<string>;
  zone: BulkUploadCommon;
  label: string;
}

export function useBulkUpload({
  knownNames,
  maxBytes,
  contentTypeOf,
  onError,
}: {
  /** Lower-cased registry filenames the page already knows (for Keep both). */
  knownNames: ReadonlySet<string>;
  maxBytes: number;
  contentTypeOf: (file: File) => string;
  onError: (message: string) => void;
}): {
  preflight: PreflightState | null;
  localUploads: LocalBatchUpload[];
  start: (items: DroppedFile[], zone: BulkUploadCommon, label: string) => Promise<void>;
  confirm: (choices: Map<number, CollisionChoice>) => Promise<void>;
  cancel: () => void;
} {
  const [preflight, setPreflight] = useState<PreflightState | null>(null);
  const [localUploads, setLocalUploads] = useState<LocalBatchUpload[]>([]);
  const pendingRef = useRef<PendingContext | null>(null);
  // Bumped on cancel / new drop so a slow fingerprint pass can't resurrect
  // a pre-flight the admin already dismissed.
  const generationRef = useRef(0);

  const patchLocal = useCallback(
    (batchId: string, fn: (u: LocalBatchUpload) => LocalBatchUpload) => {
      setLocalUploads((cur) => cur.map((u) => (u.batchId === batchId ? fn(u) : u)));
    },
    [],
  );

  // Closing the tab mid-upload loses the files not yet on the server; the
  // browser's own "leave site?" prompt is the only guard that works here.
  const uploading = localUploads.some((u) => !u.finished);
  useEffect(() => {
    if (!uploading) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [uploading]);

  const start = useCallback(
    async (items: DroppedFile[], zone: BulkUploadCommon, label: string): Promise<void> => {
      const gen = ++generationRef.current;
      const plan = planUploadNames(
        items.map((i) => ({ name: i.file.name, relativePath: i.relativePath, size: i.file.size })),
        { maxBytes },
      );
      const total = plan.toUpload.length;
      setPreflight({ phase: 'checking', label, done: 0, total });
      try {
        const md5s = new Array<string>(total);
        let done = 0;
        await runWithConcurrency(
          plan.toUpload.map((_, k) => k),
          HASH_CONCURRENCY,
          async (k) => {
            if (gen !== generationRef.current) return;
            md5s[k] = await md5Base64OfBlob(items[plan.toUpload[k].index].file);
            done += 1;
            if (gen === generationRef.current) {
              setPreflight({ phase: 'checking', label, done, total });
            }
          },
        );
        if (gen !== generationRef.current) return;

        const planned: PreflightPlannedFile[] = plan.toUpload.map((e, k) => ({
          finalName: e.finalName,
          md5Base64: md5s[k],
          originalPath: items[e.index].relativePath || items[e.index].file.name,
          renameReasons: e.renameReasons,
        }));
        const checks: DuplicateCheckItem[] = [];
        for (const group of chunk(planned, CHECK_CHUNK)) {
          const res = await apiPost<{ items: DuplicateCheckItem[] }>(
            '/api/v1/admin/knowledgebase/documents/check-duplicates',
            {
              ring: zone.ring,
              items: group.map((p) => ({ filename: p.finalName, md5Base64: p.md5Base64 })),
            },
          );
          checks.push(...res.items);
        }
        if (gen !== generationRef.current) return;

        const dispositions = classifyPlanned(planned, checks);
        const takenNames = new Set(knownNames);
        for (const c of checks) if (c.existing) takenNames.add(c.filename.toLowerCase());
        pendingRef.current = { items, plan, planned, dispositions, takenNames, zone, label };
        setPreflight({
          phase: 'ready',
          label,
          ringLabel: zone.ring === 'ring0' ? 'Internal' : 'Product',
          planned,
          dispositions,
          skippedEarly: plan.skipped.map((s) => ({
            path: items[s.index].relativePath || items[s.index].file.name,
            reason: s.reason,
          })),
          takenNames,
        });
      } catch (err) {
        if (gen !== generationRef.current) return;
        setPreflight({
          phase: 'error',
          label,
          message: err instanceof Error ? err.message : 'Could not check the files.',
        });
      }
    },
    [knownNames, maxBytes],
  );

  const cancel = useCallback(() => {
    generationRef.current += 1;
    pendingRef.current = null;
    setPreflight(null);
  }, []);

  const confirm = useCallback(
    async (choices: Map<number, CollisionChoice>): Promise<void> => {
      const ctx = pendingRef.current;
      pendingRef.current = null;
      setPreflight(null);
      if (!ctx) return;
      const resolved = resolveUploads(ctx.planned, ctx.dispositions, choices, ctx.takenNames);
      if (!resolved || resolved.uploads.length === 0) return;

      const skipped: Array<{ filename: string; reason: IngestBatchSkipReason }> = [
        ...ctx.plan.skipped.map((s) => ({
          filename: (ctx.items[s.index].relativePath || ctx.items[s.index].file.name).slice(
            0,
            1024,
          ),
          reason: s.reason,
        })),
        ...resolved.skipped.map((s) => ({
          filename: ctx.planned[s.plannedIndex].originalPath.slice(0, 1024),
          reason: s.reason,
        })),
      ].slice(0, MAX_SKIPPED_RECORDED);

      let batch: IngestBatch;
      try {
        batch = await apiPost<IngestBatch>('/api/v1/admin/knowledgebase/batches', {
          label: ctx.label.slice(0, 200),
          ring: ctx.zone.ring,
          plannedCount: resolved.uploads.length,
          skipped,
        });
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Could not start the upload.');
        return;
      }

      const files = resolved.uploads.map((u) => {
        const entry = ctx.plan.toUpload[u.plannedIndex];
        return {
          file: ctx.items[entry.index].file,
          filename: u.filename,
          overwrite: u.overwrite,
          md5Base64: ctx.planned[u.plannedIndex].md5Base64,
        };
      });
      setLocalUploads((cur) => [
        ...cur,
        {
          batchId: batch.id,
          label: ctx.label,
          total: files.length,
          handedOver: 0,
          failed: [],
          finished: false,
        },
      ]);
      try {
        await runBulkUpload({
          batchId: batch.id,
          files,
          common: ctx.zone,
          contentTypeOf,
          callbacks: {
            isCancelled: () => false,
            onFile: (index, state, detail) => {
              if (state === 'queued') {
                patchLocal(batch.id, (u) => ({ ...u, handedOver: u.handedOver + 1 }));
              } else if (state === 'error') {
                patchLocal(batch.id, (u) => ({
                  ...u,
                  failed: [
                    ...u.failed,
                    {
                      filename: files[index].filename,
                      message: detail?.message ?? 'Upload failed.',
                    },
                  ],
                }));
              }
            },
          },
        });
      } finally {
        patchLocal(batch.id, (u) => ({ ...u, finished: true }));
      }
    },
    [contentTypeOf, onError, patchLocal],
  );

  return { preflight, localUploads, start, confirm, cancel };
}
